import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchableMultiSelectListCst from "@components/SearchableMultiSelectListCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { PagesID } from "@utils/utilsConfigurations";
import { SelectedTargetItem } from "../../../Enrollment/types";
import {
  EditionEnrollableUser,
  getEnrollableUserId,
  getEnrollableUserName,
  isAlreadyInEdition,
  isLockedElsewhere,
  useEditionEnrollableUsers,
} from "../hooks/useEditionEnrollableUsers";
import UserRow from "../../../Enrollment/steps/tabs/rows/UserRow";

const PAGE_SIZE = 10;

interface EditionUsersTabProps {
  corporateId: string | number;
  objectId: number;
  editionId: number;
  selected: SelectedTargetItem[];
  onChange: (next: SelectedTargetItem[]) => void;
}

const EditionUsersTab: React.FC<EditionUsersTabProps> = ({
  corporateId,
  objectId,
  editionId,
  selected,
  onChange,
}) => {
  const { t } = useTranslation("edition-enrollment");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading } =
    useEditionEnrollableUsers({
      corporateId,
      objectId,
      editionId,
      page,
      pageSize,
      fieldFilters,
    });

  const selectedSet = useMemo(
    () => new Set<string | number>(selected.map((s) => s.id)),
    [selected],
  );

  const lockedIds = useMemo(
    () =>
      new Set<string | number>(
        items.filter(isAlreadyInEdition).map(getEnrollableUserId),
      ),
    [items],
  );

  const disabledIds = useMemo(
    () =>
      new Set<string | number>(
        items
          .filter((u) => !isAlreadyInEdition(u) && isLockedElsewhere(u))
          .map(getEnrollableUserId),
      ),
    [items],
  );

  const handleToggle = (id: string | number, next: boolean) => {
    if (next) {
      const found = items.find((u) => getEnrollableUserId(u) === String(id));
      if (!found) return;
      onChange([
        ...selected,
        { id, label: getEnrollableUserName(found), meta: found },
      ]);
    } else {
      onChange(selected.filter((s) => s.id !== id));
    }
  };

  return (
    <Box>
      <SearchableMultiSelectListCst<EditionEnrollableUser>
        items={items}
        getId={(u) => getEnrollableUserId(u)}
        renderRow={({ item }) => (
          <UserRow
            id={getEnrollableUserId(item)}
            name={getEnrollableUserName(item)}
            email={item.userEmail}
            badges={
              isAlreadyInEdition(item)
                ? [
                    {
                      label: String(t("users.enrolled-badge")),
                      variant: "active",
                    },
                  ]
                : undefined
            }
          />
        )}
        selectedIds={selectedSet}
        lockedIds={lockedIds}
        disabledIds={disabledIds}
        onToggle={handleToggle}
        onSearchSubmit={() => {}}
        searchPlaceholder=""
        searchButtonLabel=""
        hideSearch
        headerSlot={
          <FiltersAccordionToggle
            pageId={PagesID["enrollment.edition.user"]}
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
          />
        }
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        isLoading={isLoading}
        selectedCountLabel={String(
          t("users.selected-count", { count: selected.length }),
        )}
        emptyLabel={String(t("users.empty"))}
      />
    </Box>
  );
};

export default EditionUsersTab;
